import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit2, Save, ToggleLeft, ToggleRight } from 'react-feather';
import useWebSocket from '@/hooks/useWebSocket';
import events from '@/constants/websocketEvents';
import { toast } from 'react-toastify';

const ProductsKDSTab = () => {
    const [products, setProducts] = useState([]);
    const [editingProduct, setEditingProduct] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [restaurants, setRestaurants] = useState([]);
    const [currentRestaurantId, setCurrentRestaurantId] = useState(null);

    useEffect(() => {        
        fetchRestaurants();
    }, []);

    useEffect(() => {
        if (currentRestaurantId) {
            fetchProducts(currentRestaurantId);
        }
    }, [currentRestaurantId]);

    // Gestion des mises à jour en temps réel
    const handleProductUpdate = (data) => {
        console.log('Mise à jour produit reçue:', data);
        if (data.restaurantId === currentRestaurantId) {
            setProducts(prevProducts => 
                prevProducts.map(product => 
                    product.product_id === data.productId
                        ? { ...product, quantity: data.updates.quantity }
                        : product
                )
            );
        }
    };

    // Écoute des événements WebSocket
    useWebSocket(events.PRODUCT_UPDATED, handleProductUpdate);
    useWebSocket(events.ORDER_STATUS_UPDATED, () => {
        if (currentRestaurantId) {
            fetchProducts(currentRestaurantId);
        }
    });
    useWebSocket(events.ORDER_CREATED, (order) => {
        console.log('Nouvelle commande reçue:', order);
        if (currentRestaurantId) {
            fetchProducts(currentRestaurantId);
        }
    });
    useWebSocket(events.TEMPORARY_ORDER_CREATED, () => {
        if (currentRestaurantId) {
            fetchProducts(currentRestaurantId);
        }
    });

    const fetchRestaurants = async () => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/restaurants`);
            if (response.status === 200) {
                // Filtrer pour exclure le restaurant no-limit
                const filteredRestaurants = response.data;
                setRestaurants(filteredRestaurants);
                if (filteredRestaurants.length > 0) {
                    setCurrentRestaurantId(filteredRestaurants[0].id);
                }
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des restaurants:', error);
        }
    };

    const fetchProducts = async (restaurantId) => {
        console.log('Restaurant ID:', restaurantId);
        if (!restaurantId) return;
        try {
            setLoading(true);
            const [regularProducts, noLimitProducts] = await Promise.all([
                // Récupérer les produits réguliers
                axios.get(
                    `${import.meta.env.VITE_API_URL}/api/restaurants/${restaurantId}/products/list`
                ),
                // Récupérer les produits no limit
                axios.get(
                    `${import.meta.env.VITE_API_URL}/api/restaurants/no-limit/products/list`
                )
            ]);

            const allProducts = [
                ...(regularProducts.data || []),
                ...(noLimitProducts.data?.products || [])
            ];

            console.log('Produits récupérés:', allProducts);
            setProducts(allProducts);
        } catch (error) {
            console.error('Erreur lors de la récupération des produits:', error);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRestaurantChange = (restaurantId) => {
        setCurrentRestaurantId(restaurantId);
    };

    const handleQuantityChange = async (productId, quantity) => {
        try {
            await axios.patch(
                `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${currentRestaurantId}/products/${productId}`,
                { quantity: quantity }
            );
            setEditingProduct(null);
            // On ne recharge plus tous les produits car le WebSocket nous enverra la mise à jour
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la quantité:', error);
        }
    };

    const handleToggleActive = async (productId, currentStatus) => {
        try {
            const response = await axios.patch(
                `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${currentRestaurantId}/products/${productId}/toggle-active`,
                { is_active: !currentStatus }
            );
            
            if (response.status === 200) {
                setProducts(prevProducts =>
                    prevProducts.map(product =>
                        product.product_id === productId
                            ? { ...product, is_active: !currentStatus }
                            : product
                    )
                );
                toast.success(currentStatus ? 'Produit désactivé' : 'Produit activé', {
                    position: 'top-center',
                    autoClose: 800,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: true,
                    theme: 'colored',
                });
            }
        } catch (error) {
            console.error('Erreur lors de la modification du statut:', error);
            toast.error('Erreur lors de la modification du statut', {
                position: 'top-center',
                autoClose: 800,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
                draggable: true,
                theme: 'colored',
            });
        }
    };

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getQuantityStyle = (quantity) => {
        if (quantity === 0) {
            return 'bg-red-50 border-2 border-red-500';
        }
        if (quantity < 10) {
            return 'bg-red-50 border border-red-300';
        }
        return 'bg-white';
    };

    const getQuantityTextStyle = (quantity) => {
        if (quantity === 0) {
            return 'text-red-600 font-bold';
        }
        if (quantity < 10) {
            return 'text-red-500';
        }
        return 'text-gray-600';
    };

    return (
        <div className="p-4">
            <div className="mb-6 flex flex-col gap-4">
                <div className="flex flex-wrap gap-2">
                    {restaurants.map((restaurant) => (
                        <button
                            key={restaurant.id}
                            onClick={() => handleRestaurantChange(restaurant.id)}
                            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                                currentRestaurantId === restaurant.id
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            {restaurant.name}
                        </button>
                    ))}
                </div>
                <input
                    type="text"
                    placeholder="Rechercher un produit..."
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProducts.map((product, index) => (
                        <div key={product.product_id || index} 
                             className={`rounded-lg shadow-md p-4 transition-all duration-200 ${getQuantityStyle(product.quantity)} ${!product.is_active ? 'opacity-50' : ''}`}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold truncate">{product.name}</h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-500">{product.category}</span>
                                    <button
                                        onClick={() => handleToggleActive(product.product_id, product.is_active)}
                                        className={`p-2 ${product.is_active ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'}`}
                                    >
                                        {product.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <span className={getQuantityTextStyle(product.quantity)}>
                                    {product.quantity === 0 ? 'Victime de son succès' : 'Quantité:'}
                                </span>
                                {editingProduct === product.product_id ? (
                                    <div className="flex items-center">
                                        <input
                                            type="number"
                                            className="w-20 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
                                            value={product.quantity}
                                            onChange={(e) => {
                                                const newProducts = products.map(p =>
                                                    p.product_id === product.product_id 
                                                    ? {...p, quantity: parseInt(e.target.value)} 
                                                    : p
                                                );
                                                setProducts(newProducts);
                                            }}
                                            min="0"
                                        />
                                        <button
                                            className="ml-2 p-2 text-green-600 hover:text-green-800"
                                            onClick={() => handleQuantityChange(product.product_id, product.quantity)}
                                        >
                                            <Save size={20} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <span className={`mr-2 font-semibold ${getQuantityTextStyle(product.quantity)}`}>
                                            {product.quantity}
                                        </span>
                                        <button
                                            className="p-2 text-blue-600 hover:text-blue-800"
                                            onClick={() => setEditingProduct(product.product_id)}
                                        >
                                            <Edit2 size={20} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && filteredProducts.length === 0 && (
                <div className="flex justify-center items-center h-64">
                    <p className="text-gray-500">Aucun produit trouvé</p>
                </div>
            )}
        </div>
    );
};

export default ProductsKDSTab; 